<template>
  <div
    v-if="loading"
    class="preservice-dashboard preservice-dashboard--loading"
  >
    <div class="loading-card">
      <div class="loading-card__icon">
        <v-icon size="34" color="#2563eb">mdi-school-outline</v-icon>
      </div>
      <div class="loading-card__title">Loading preservice dashboard</div>
      <div class="loading-card__subtitle">
        Fetching current summary, charts, and discipline breakdown.
      </div>
      <v-progress-linear
        class="loading-card__progress"
        color="primary"
        height="6"
        indeterminate
        rounded
      ></v-progress-linear>
    </div>
  </div>

  <div v-else class="preservice-dashboard">
    <div class="preservice-shell">
      <div class="preservice-hero">
        <div class="preservice-hero__left">
          <div class="preservice-hero__icon">
            <v-icon size="28" color="#2563eb">mdi-school-outline</v-icon>
          </div>
          <div>
            <h1 class="preservice-hero__title">Preservice Dashboard</h1>
            <p class="preservice-hero__subtitle">
              Explore student distribution by program level, discipline, and
              education program.
            </p>
          </div>
        </div>
        <div class="preservice-hero__right">
          <div class="preservice-hero__stat-label">
            Unique students in current view
          </div>
          <div class="preservice-hero__stat-value">
            {{ formatNumber(uniqueStudentTotal) }}
          </div>
          <div class="preservice-hero__stat-meta">{{ activeFilterLabel }}</div>
        </div>
      </div>

      <v-card class="preservice-panel preservice-panel--filters" flat>
        <div class="panel-header">
          <div>
            <div class="panel-eyebrow">Dashboard Filters</div>
            <div class="panel-title">Refine the current view</div>
          </div>
          <div class="panel-badge">
            <v-icon size="16" class="mr-1">mdi-filter-variant</v-icon>
            {{ activeFilterCount }} active
          </div>
        </div>

        <v-row class="mt-1">
          <v-col cols="12" sm="6" lg="3">
            <v-autocomplete
              v-model="universityValue"
              :clearable="!isUniversityScopedUser && clearable"
              :disabled="isUniversityScopedUser"
              :items="UNIVERSITIES"
              :loading="universityLoading"
              class="modern-input"
              dense
              hide-details
              item-text="name"
              item-value="id"
              label="Higher Learning Institution"
              outlined
              return-object
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" sm="6" lg="3">
            <v-autocomplete
              v-model="educLevelValue"
              :clearable="clearable"
              :items="EDUCATION_LEVEL"
              :loading="educLevelLoading"
              class="modern-input"
              dense
              hide-details
              item-text="displayName"
              item-value="value"
              label="Education Level"
              outlined
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" sm="6" lg="3">
            <v-autocomplete
              v-model="programValue"
              :clearable="clearable"
              :items="EDUCATIONAL_PROGRAMS"
              :loading="educationalProgramLoading"
              class="modern-input"
              dense
              hide-details
              item-text="name"
              item-value="id"
              label="Program Education Type"
              outlined
              return-object
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" sm="6" lg="3">
            <v-autocomplete
              v-model="yearOfEducValue"
              :clearable="clearable"
              :items="YEAR_OF_EDUCATION"
              :loading="yearOfEducationLoading"
              class="modern-input"
              dense
              hide-details
              item-text="name"
              item-value="id"
              label="Year of Education"
              outlined
            ></v-autocomplete>
          </v-col>
        </v-row>
      </v-card>

      <div class="summary-section">
        <div class="section-header">
          <div>
            <div class="section-title">Program Summary</div>
            <div class="section-subtitle">
              Unique FAN count within each preservice level
            </div>
          </div>
        </div>

        <v-row>
          <v-col
            v-for="card in summaryCards"
            :key="card.key"
            cols="12"
            sm="6"
            xl="2"
          >
            <v-card
              :aria-label="`Open ${card.label} records`"
              :to="card.route"
              class="summary-card"
              flat
            >
              <div class="summary-card__top">
                <div
                  class="summary-card__icon"
                  :style="{
                    background: card.iconBackground,
                    color: card.iconColor,
                  }"
                >
                  <v-icon size="20">{{ card.icon }}</v-icon>
                </div>
                <div class="summary-card__share">
                  {{ getCardShare(card.key) }}%
                </div>
              </div>
              <div class="summary-card__label">{{ card.label }}</div>
              <div class="summary-card__value">
                <ICountUp
                  :delay="delay"
                  :endVal="preServiceSummary[card.key] || 0"
                  :options="options"
                  :startVal="count"
                />
              </div>
              <div class="summary-card__meta">{{ card.description }}</div>
              <div class="summary-card__progress">
                <span
                  :style="{
                    width: `${getCardShare(card.key)}%`,
                    background: card.progress,
                  }"
                ></span>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </div>

      <div class="charts-section">
        <div class="section-header">
          <div>
            <div class="section-title">Visual Breakdown</div>
            <div class="section-subtitle">
              Quick composition view for disciplines and education programs
            </div>
          </div>
        </div>

        <v-row>
          <v-col cols="12" lg="6">
            <v-card class="chart-card" flat>
              <div class="chart-card__header">
                <div>
                  <div class="chart-card__title">Students by Discipline</div>
                  <div class="chart-card__subtitle">
                    {{ disciplineInsight }}
                  </div>
                </div>
                <div class="chart-card__chip">
                  <v-icon size="16" class="mr-1">mdi-chart-donut</v-icon>
                  {{ preServiceProfession.length }} disciplines
                </div>
              </div>

              <div v-if="STUDENT_DISCIPLINES.length" class="chart-card__body">
                <apexchart
                  :options="discipline_Options"
                  :series="STUDENT_DISCIPLINES"
                  height="340"
                  type="donut"
                ></apexchart>
              </div>
              <div v-else class="chart-empty">
                <v-icon size="22" color="#94a3b8">mdi-chart-donut</v-icon>
                <span
                  >No discipline data available for the selected filters.</span
                >
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" lg="6">
            <v-card class="chart-card" flat>
              <div class="chart-card__header">
                <div>
                  <div class="chart-card__title">
                    Students by Program Education Type
                  </div>
                  <div class="chart-card__subtitle">{{ programInsight }}</div>
                </div>
                <div class="chart-card__chip chart-card__chip--green">
                  <v-icon size="16" class="mr-1">mdi-school</v-icon>
                  {{ preServiceEducationProgram.length }} programs
                </div>
              </div>

              <div v-if="EDUCATION_PROGRAMS.length" class="chart-card__body">
                <apexchart
                  :options="education_program_Options"
                  :series="EDUCATION_PROGRAMS"
                  height="340"
                  type="donut"
                ></apexchart>
              </div>
              <div v-else class="chart-empty">
                <v-icon size="22" color="#94a3b8">mdi-chart-donut</v-icon>
                <span
                  >No program education type data available for the selected
                  filters.</span
                >
              </div>
            </v-card>
          </v-col>
        </v-row>
      </div>

      <v-card class="preservice-table-card" flat>
        <div class="panel-header panel-header--table">
          <div>
            <div class="panel-eyebrow">Discipline Table</div>
            <div class="panel-title">Unique student counts by profession</div>
          </div>
          <div class="table-summary">
            <div class="table-summary__item">
              <span class="table-summary__label">Rows</span>
              <span class="table-summary__value">{{
                professionTableRows.length
              }}</span>
            </div>
            <div class="table-summary__item">
              <span class="table-summary__label">Top discipline</span>
              <span class="table-summary__value">{{
                topProfession ? topProfession.profession : "No data"
              }}</span>
            </div>
          </div>
        </div>

        <v-data-table
          :headers="headers"
          :items="professionTableRows"
          :items-per-page="8"
          class="preservice-table"
          loading-text="Loading... please wait"
        >
          <template v-slot:item.rank="{ item }">
            <div class="rank-pill">{{ item.rank }}</div>
          </template>

          <template v-slot:item.profession="{ item }">
            <div class="profession-cell">
              <div class="profession-cell__title">{{ item.profession }}</div>
              <div class="profession-cell__subtitle">
                Students attending multiple departments appear in each department
              </div>
            </div>
          </template>

          <template v-slot:item.count="{ item }">
            <div class="count-cell">{{ formatNumber(item.count) }}</div>
          </template>

          <template v-slot:item.share="{ item }">
            <div class="share-cell">
              <div class="share-cell__meta">
                <span>{{ item.share }}%</span>
                <span>{{ formatNumber(item.count) }} unique students</span>
              </div>
              <div class="share-cell__bar">
                <span :style="{ width: `${item.share}%` }"></span>
              </div>
            </div>
          </template>

          <template v-slot:no-data>
            <div class="table-empty">
              <v-icon size="22" color="#94a3b8">mdi-table-search</v-icon>
              <span>No disciplines found for the selected filters.</span>
            </div>
          </template>
        </v-data-table>
      </v-card>
    </div>
  </div>
</template>

<script>
import { PreserviceService } from "./preservice.service";
import ICountUp from "vue-countup-v2";
import {
  getAssignedUniversityOption,
  getEducationalPrograms,
  getStudentStatuses,
  getUniversities,
  isUniversityScopedPreserviceUser,
} from "../../utils/helper";
import { EducLevelEnum, YearOfEducEnum } from "@/utils/enums";

export default {
  name: "VueChart",
  components: {
    ICountUp,
  },
  data() {
    return {
      count: 0,
      delay: 500,
      options: {
        useEasing: true,
        useGrouping: true,
        separator: ",",
        decimal: ".",
        prefix: "",
        suffix: "",
      },
      preServiceSummary: {},
      preServiceProfession: [],
      preServiceEducationProgram: [],
      uniqueStudentTotal: 0,
      programSummaryCards: [
        {
          key: "TVETProgram",
          label: "TVET Program",
          route: "/pre-service/tvet-program",
          description: "Technical and vocational education pipeline",
          icon: "mdi-hammer-wrench",
          iconBackground: "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)",
          iconColor: "#1d4ed8",
          progress: "linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)",
        },
        {
          key: "GenericEntry",
          label: "Generic Entry",
          route: "/pre-service/generic-entry",
          description: "Students in standard undergraduate intake",
          icon: "mdi-account-school-outline",
          iconBackground: "linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)",
          iconColor: "#6d28d9",
          progress: "linear-gradient(90deg, #8b5cf6 0%, #7c3aed 100%)",
        },
        {
          key: "PostBasic",
          label: "Post Basic",
          route: "/pre-service/post-basic",
          description: "Students progressing through post-basic tracks",
          icon: "mdi-book-open-variant",
          iconBackground: "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)",
          iconColor: "#15803d",
          progress: "linear-gradient(90deg, #22c55e 0%, #16a34a 100%)",
        },
        {
          key: "PostGraduate",
          label: "Post Graduate",
          route: "/pre-service/post-graduate",
          description: "Advanced professional specialization programs",
          icon: "mdi-school",
          iconBackground: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
          iconColor: "#b45309",
          progress: "linear-gradient(90deg, #f59e0b 0%, #d97706 100%)",
        },
        {
          key: "NimeiProgram",
          label: "Graduate Entry",
          route: "/pre-service/graduate-entry",
          description: "Graduate-entry pathway across supported programs",
          icon: "mdi-account-tie-hat",
          iconBackground: "linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)",
          iconColor: "#be123c",
          progress: "linear-gradient(90deg, #f43f5e 0%, #e11d48 100%)",
        },
      ],
      headers: [
        { text: "#", value: "rank", sortable: false, width: "72px" },
        { text: "Profession Type", value: "profession", sortable: false },
        { text: "Unique Students", value: "count", sortable: false, align: "end" },
        { text: "Share", value: "share", sortable: false },
      ],
      loading: false,

      STUDENT_DISCIPLINES: [],
      EDUCATION_PROGRAMS: [],

      discipline_Options: {
        chart: {
          toolbar: {
            show: false,
          },
        },
        colors: [
          "#2563eb",
          "#7c3aed",
          "#0f766e",
          "#ea580c",
          "#e11d48",
          "#0891b2",
          "#65a30d",
          "#4f46e5",
        ],
        labels: [],
        legend: {
          position: "bottom",
          fontSize: "13px",
          fontFamily: "inherit",
          labels: {
            colors: "#475569",
          },
          itemMargin: {
            horizontal: 10,
            vertical: 6,
          },
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          width: 4,
          colors: ["#ffffff"],
        },
        tooltip: {
          y: {
            formatter(value) {
              return `${Number(value).toLocaleString()} students`;
            },
          },
        },
        noData: {
          text: "No data available",
          align: "center",
          verticalAlign: "middle",
          style: {
            color: "#94a3b8",
            fontSize: "14px",
          },
        },
        plotOptions: {
          pie: {
            expandOnClick: false,
            donut: {
              size: "72%",
              labels: {
                show: true,
                name: {
                  show: true,
                  fontSize: "13px",
                  color: "#64748b",
                  offsetY: -10,
                },
                value: {
                  show: true,
                  fontSize: "24px",
                  fontWeight: 700,
                  color: "#0f172a",
                  offsetY: 10,
                  formatter(value) {
                    return Number(value).toLocaleString();
                  },
                },
                total: {
                  show: true,
                  showAlways: true,
                  label: "Department participations",
                  color: "#64748b",
                  fontSize: "13px",
                  fontWeight: 600,
                  formatter(w) {
                    const total = w.globals.seriesTotals.reduce(
                      (sum, value) => sum + value,
                      0,
                    );
                    return Number(total).toLocaleString();
                  },
                },
              },
            },
          },
        },
        responsive: [
          {
            breakpoint: 960,
            options: {
              legend: {
                position: "bottom",
              },
            },
          },
        ],
      },
      education_program_Options: {
        chart: {
          toolbar: {
            show: false,
          },
        },
        colors: [
          "#0f766e",
          "#2563eb",
          "#9333ea",
          "#ca8a04",
          "#db2777",
          "#0284c7",
          "#4d7c0f",
          "#c2410c",
        ],
        labels: [],
        legend: {
          position: "bottom",
          fontSize: "13px",
          fontFamily: "inherit",
          labels: {
            colors: "#475569",
          },
          itemMargin: {
            horizontal: 10,
            vertical: 6,
          },
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          width: 4,
          colors: ["#ffffff"],
        },
        tooltip: {
          y: {
            formatter(value) {
              return `${Number(value).toLocaleString()} students`;
            },
          },
        },
        noData: {
          text: "No data available",
          align: "center",
          verticalAlign: "middle",
          style: {
            color: "#94a3b8",
            fontSize: "14px",
          },
        },
        plotOptions: {
          pie: {
            expandOnClick: false,
            donut: {
              size: "72%",
              labels: {
                show: true,
                name: {
                  show: true,
                  fontSize: "13px",
                  color: "#64748b",
                  offsetY: -10,
                },
                value: {
                  show: true,
                  fontSize: "24px",
                  fontWeight: 700,
                  color: "#0f172a",
                  offsetY: 10,
                  formatter(value) {
                    return Number(value).toLocaleString();
                  },
                },
                total: {
                  show: true,
                  showAlways: true,
                  label: "Program participations",
                  color: "#64748b",
                  fontSize: "13px",
                  fontWeight: 600,
                  formatter(w) {
                    const total = w.globals.seriesTotals.reduce(
                      (sum, value) => sum + value,
                      0,
                    );
                    return Number(total).toLocaleString();
                  },
                },
              },
            },
          },
        },
        responsive: [
          {
            breakpoint: 960,
            options: {
              legend: {
                position: "bottom",
              },
            },
          },
        ],
      },

      EDUCATIONAL_PROGRAMS: [],
      UNIVERSITIES: [],
      YEAR_OF_EDUCATION: [],
      EDUCATIONAL_STATUS: [],
      EDUCATION_LEVEL: [],

      universityLoading: false,
      yearOfEducationLoading: false,
      educationalProgramLoading: false,
      educStatusLoading: false,
      educLevelLoading: false,
      clearable: true,

      programValue: null,
      universityValue: null,
      yearOfEducValue: null,
      educLevelValue: null,
      currentYear: new Date().getFullYear(),
      isInitializing: true,
    };
  },
  computed: {
    isUniversityScopedUser() {
      return isUniversityScopedPreserviceUser();
    },
    summaryCards() {
      return this.programSummaryCards;
    },
    summaryTotal() {
      return this.summaryCards.reduce(
        (total, card) =>
          total + (Number(this.preServiceSummary[card.key]) || 0),
        0,
      );
    },
    disciplineParticipationTotal() {
      return this.preServiceProfession.reduce(
        (total, item) => total + (Number(item.count) || 0),
        0,
      );
    },
    professionTableRows() {
      return this.preServiceProfession.map((item, index) => ({
        ...item,
        rank: index + 1,
        share: this.calculateShare(item.count, this.disciplineParticipationTotal),
      }));
    },
    topProfession() {
      return this.preServiceProfession.length
        ? this.preServiceProfession[0]
        : null;
    },
    topProgram() {
      return this.preServiceEducationProgram.length
        ? this.preServiceEducationProgram[0]
        : null;
    },
    disciplineInsight() {
      if (!this.topProfession) {
        return "No discipline records available in the selected view.";
      }

      return `${this.topProfession.profession} leads with ${this.formatNumber(this.topProfession.count)} unique students.`;
    },
    programInsight() {
      if (!this.topProgram) {
        return "No education program records available in the selected view.";
      }

      return `${this.topProgram.educationProgram} leads with ${this.formatNumber(this.topProgram.count)} unique students.`;
    },
    activeFilterCount() {
      return [
        this.universityValue,
        this.educLevelValue,
        this.programValue,
        this.yearOfEducValue,
      ].filter(Boolean).length;
    },
    activeFilterLabel() {
      if (!this.activeFilterCount) {
        return "No filters applied";
      }

      return `${this.activeFilterCount} filter${this.activeFilterCount > 1 ? "s" : ""} applied`;
    },
  },
  watch: {
    universityValue: {
      handler() {
        if (!this.isInitializing) {
          this.fetchPreServiceSummary();
        }
      },
    },
    educLevelValue: {
      handler() {
        if (!this.isInitializing) {
          this.fetchPreServiceSummary();
        }
      },
    },
    programValue: {
      handler() {
        if (!this.isInitializing) {
          this.fetchPreServiceSummary();
        }
      },
    },
    yearOfEducValue: {
      handler() {
        if (!this.isInitializing) {
          this.fetchPreServiceSummary();
        }
      },
    },
  },
  async created() {
    await this.initateLookups();
    this.isInitializing = false;
    await this.fetchPreServiceSummary();
  },
  methods: {
    normalizePreServiceLevel(value) {
      const normalizedValue = (value || "")
        .toString()
        .replace(/\s+/g, "")
        .toLowerCase();

      const levelMappings = {
        tvetprogram: "TVETProgram",
        genericentry: "GenericEntry",
        postbasic: "PostBasic",
        postgraduate: "PostGraduate",
        nimeiprogram: "NimeiProgram",
        graduateentry: "NimeiProgram",
      };

      return levelMappings[normalizedValue] || value;
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString();
    },
    calculateShare(value, total = this.summaryTotal) {
      if (!total) {
        return 0;
      }

      return Math.round((Number(value || 0) / total) * 100);
    },
    getCardShare(key) {
      return this.calculateShare(this.preServiceSummary[key]);
    },
    getEducationLevelOptions() {
      return EducLevelEnum.map((item) => {
        const value = this.normalizePreServiceLevel(item.name);
        return {
          ...item,
          value,
          displayName: value === "NimeiProgram" ? "Graduate Entry" : item.name,
        };
      });
    },
    getYearOfEducationOptions() {
      const uniqueYears = new Map();

      YearOfEducEnum.forEach((item) => {
        if (!uniqueYears.has(item.id)) {
          uniqueYears.set(item.id, {
            ...item,
            name: item.id === 0 ? "1st Year" : item.name,
          });
        }
      });

      return Array.from(uniqueYears.values());
    },
    getSelectedYearOffset() {
      if (this.yearOfEducValue == null) {
        return null;
      }

      return typeof this.yearOfEducValue === "object"
        ? this.yearOfEducValue.id
        : this.yearOfEducValue;
    },
    buildSummarySearchParams() {
      const searchParams = new URLSearchParams();
      const selectedYearOffset = this.getSelectedYearOffset();

      if (this.educLevelValue) {
        searchParams.append("preServiceLevel", this.educLevelValue);
      }
      if (this.universityValue && this.universityValue.id) {
        searchParams.append("universityId", this.universityValue.id);
      }
      if (this.programValue && this.programValue.id) {
        searchParams.append("educationProgramId", this.programValue.id);
      }
      if (selectedYearOffset != null) {
        searchParams.append(
          "entryYear",
          this.currentYear - Number(selectedYearOffset),
        );
      }

      return searchParams.toString();
    },
    resetDashboardData() {
      this.preServiceSummary = {};
      this.preServiceProfession = [];
      this.preServiceEducationProgram = [];
      this.uniqueStudentTotal = 0;
      this.STUDENT_DISCIPLINES = [];
      this.EDUCATION_PROGRAMS = [];
      this.discipline_Options.labels = [];
      this.education_program_Options.labels = [];
    },
    async fetchPreServiceSummary() {
      this.loading = true;
      this.resetDashboardData();

      try {
        const searchParams = this.buildSummarySearchParams();
        const preServiceSumResp =
          await PreserviceService.getPostServices(searchParams);
        const data = Array.isArray(
          preServiceSumResp &&
            preServiceSumResp.data &&
            preServiceSumResp.data.rows,
        )
          ? preServiceSumResp.data.rows
          : [];

        const uniqueStudents = new Set();
        const studentKeysByLevel = new Map();
        const studentKeysByProfession = new Map();
        const studentKeysByProgram = new Map();
        const addStudentKey = (map, category, studentKey) => {
          if (!map.has(category)) {
            map.set(category, new Set());
          }
          map.get(category).add(studentKey);
        };

        data.forEach((item) => {
          const preServiceLevel = this.normalizePreServiceLevel(
            item.preServiceLevel,
          );
          const profession =
            (item && item.discipline && item.discipline.name) ||
            item.department ||
            "Unknown";
          const educationProgram =
            (item && item.educationProgram && item.educationProgram.name) ||
            "Unknown";
          const normalizedFan = String(item.idNo || "").replace(/\D/g, "");
          const studentKey =
            normalizedFan.length === 16
              ? `fan:${normalizedFan}`
              : `record:${item.id}`;

          uniqueStudents.add(studentKey);
          addStudentKey(studentKeysByLevel, preServiceLevel, studentKey);
          addStudentKey(studentKeysByProfession, profession, studentKey);
          addStudentKey(studentKeysByProgram, educationProgram, studentKey);
        });

        this.uniqueStudentTotal = uniqueStudents.size;
        studentKeysByLevel.forEach((studentKeys, preServiceLevel) => {
          this.preServiceSummary[preServiceLevel] = studentKeys.size;
        });
        this.preServiceProfession = Array.from(
          studentKeysByProfession,
          ([profession, studentKeys]) => ({
            profession,
            count: studentKeys.size,
          }),
        );
        this.preServiceEducationProgram = Array.from(
          studentKeysByProgram,
          ([educationProgram, studentKeys]) => ({
            educationProgram,
            count: studentKeys.size,
          }),
        );

        this.preServiceProfession.sort(
          (a, b) =>
            b.count - a.count || a.profession.localeCompare(b.profession),
        );
        this.preServiceEducationProgram.sort(
          (a, b) =>
            b.count - a.count ||
            a.educationProgram.localeCompare(b.educationProgram),
        );

        this.STUDENT_DISCIPLINES = this.preServiceProfession.map(
          (item) => Number(item.count) || 0,
        );
        this.discipline_Options.labels = this.preServiceProfession.map(
          (item) => item.profession,
        );

        this.EDUCATION_PROGRAMS = this.preServiceEducationProgram.map(
          (item) => Number(item.count) || 0,
        );
        this.education_program_Options.labels =
          this.preServiceEducationProgram.map((item) => item.educationProgram);
      } catch (error) {
        this.$toast.error("Unable to load preservice dashboard data.");
      } finally {
        this.loading = false;
      }
    },
    async initateLookups() {
      this.universityLoading = true;
      const univResp = await getUniversities();
      this.UNIVERSITIES =
        univResp.rows && univResp.rows.length > 0
          ? univResp.rows.filter((data) => data.isActive === true)
          : [];
      if (this.isUniversityScopedUser) {
        const assignedUniversity = getAssignedUniversityOption();
        if (assignedUniversity) {
          this.UNIVERSITIES = this.UNIVERSITIES.filter(
            (data) => Number(data.id) === Number(assignedUniversity.id),
          );
          if (!this.UNIVERSITIES.length) {
            this.UNIVERSITIES = [assignedUniversity];
          }
          this.universityValue = assignedUniversity;
        }
      }
      this.universityLoading = false;

      this.educationalProgramLoading = true;
      this.EDUCATIONAL_PROGRAMS = await getEducationalPrograms();
      this.EDUCATIONAL_PROGRAMS = this.EDUCATIONAL_PROGRAMS.rows || [];
      this.educationalProgramLoading = false;

      this.yearOfEducationLoading = true;
      this.YEAR_OF_EDUCATION = this.getYearOfEducationOptions();
      this.yearOfEducationLoading = false;

      this.educLevelLoading = true;
      this.EDUCATION_LEVEL = this.getEducationLevelOptions();
      this.educLevelLoading = false;

      this.educStatusLoading = true;
      this.EDUCATIONAL_STATUS = await getStudentStatuses();
      this.educStatusLoading = false;
    },
  },
};
</script>

<style scoped>
.preservice-dashboard {
  min-height: 100vh;
  background: radial-gradient(
      circle at top left,
      rgba(59, 130, 246, 0.14),
      transparent 30%
    ),
    radial-gradient(
      circle at top right,
      rgba(16, 185, 129, 0.12),
      transparent 28%
    ),
    linear-gradient(180deg, #f8fbff 0%, #f5f7fb 100%);
}

.preservice-dashboard--loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
}

.preservice-shell {
  max-width: 1480px;
  margin: 0 auto;
  padding: 24px;
}

.loading-card {
  width: min(460px, 100%);
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 24px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.08);
  padding: 28px;
  text-align: center;
}

.loading-card__icon {
  width: 72px;
  height: 72px;
  margin: 0 auto 18px;
  border-radius: 22px;
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-card__title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #0f172a;
}

.loading-card__subtitle {
  margin-top: 8px;
  color: #64748b;
  line-height: 1.5;
}

.loading-card__progress {
  margin-top: 20px;
}

.preservice-hero {
  display: flex;
  justify-content: space-between;
  gap: 24px;
  align-items: center;
  padding: 26px 28px;
  margin-bottom: 24px;
  border-radius: 28px;
  background: linear-gradient(
      135deg,
      rgba(37, 99, 235, 0.96) 0%,
      rgba(30, 64, 175, 0.96) 100%
    ),
    #1d4ed8;
  box-shadow: 0 24px 50px rgba(37, 99, 235, 0.22);
  color: #ffffff;
}

.preservice-hero__left {
  display: flex;
  align-items: center;
  gap: 18px;
}

.preservice-hero__icon {
  width: 62px;
  height: 62px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.14);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
}

.preservice-hero__title {
  margin: 0;
  font-size: 1.65rem;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.preservice-hero__subtitle {
  margin: 8px 0 0;
  max-width: 620px;
  color: rgba(255, 255, 255, 0.82);
  line-height: 1.6;
}

.preservice-hero__right {
  min-width: 220px;
  padding: 18px 20px;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.16);
  text-align: right;
}

.preservice-hero__stat-label {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(255, 255, 255, 0.72);
}

.preservice-hero__stat-value {
  margin-top: 6px;
  font-size: 2rem;
  font-weight: 800;
  letter-spacing: -0.03em;
}

.preservice-hero__stat-meta {
  margin-top: 6px;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
}

.preservice-panel,
.preservice-table-card,
.chart-card,
.summary-card {
  border-radius: 24px !important;
  background: rgba(255, 255, 255, 0.92) !important;
  border: 1px solid rgba(148, 163, 184, 0.16);
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.06);
}

.preservice-panel {
  padding: 22px;
  margin-bottom: 24px;
}

.panel-header,
.section-header,
.chart-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.panel-eyebrow {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 700;
  color: #64748b;
}

.panel-title,
.section-title,
.chart-card__title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
}

.section-subtitle,
.chart-card__subtitle {
  margin-top: 6px;
  color: #64748b;
  font-size: 0.92rem;
}

.panel-badge,
.chart-card__chip {
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 999px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1d4ed8;
  font-size: 0.82rem;
  font-weight: 700;
  white-space: nowrap;
}

.chart-card__chip--green {
  background: #ecfdf5;
  border-color: #a7f3d0;
  color: #047857;
}

.summary-section,
.charts-section {
  margin-bottom: 24px;
}

.summary-card {
  height: 100%;
  padding: 20px;
  overflow: hidden;
  text-decoration: none;
  transition:
    transform 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.summary-card:hover,
.summary-card:focus-visible {
  transform: translateY(-2px);
  border-color: #bfdbfe;
  box-shadow: 0 12px 24px rgba(37, 99, 235, 0.1);
}

.summary-card__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.summary-card__icon {
  width: 48px;
  height: 48px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.summary-card__share {
  font-size: 0.82rem;
  font-weight: 700;
  color: #475569;
  padding: 6px 10px;
  border-radius: 999px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.summary-card__label {
  margin-top: 18px;
  font-size: 0.95rem;
  font-weight: 700;
  color: #334155;
}

.summary-card__value {
  margin-top: 10px;
  font-size: 2rem;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.03em;
}

.summary-card__meta {
  margin-top: 8px;
  min-height: 42px;
  color: #64748b;
  font-size: 0.86rem;
  line-height: 1.5;
}

.summary-card__progress {
  margin-top: 18px;
  height: 8px;
  border-radius: 999px;
  background: #e2e8f0;
  overflow: hidden;
}

.summary-card__progress span {
  display: block;
  height: 100%;
  border-radius: inherit;
}

.chart-card {
  height: 100%;
  padding: 22px;
}

.chart-card__body {
  margin-top: 18px;
}

.chart-empty,
.table-empty {
  min-height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #64748b;
  text-align: center;
  padding: 24px;
}

.preservice-table-card {
  padding: 20px 20px 8px;
}

.panel-header--table {
  margin-bottom: 10px;
}

.table-summary {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.table-summary__item {
  min-width: 130px;
  padding: 10px 14px;
  border-radius: 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.table-summary__label {
  display: block;
  font-size: 0.74rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 700;
  color: #64748b;
}

.table-summary__value {
  display: block;
  margin-top: 4px;
  color: #0f172a;
  font-weight: 700;
}

.preservice-table {
  background: transparent;
}

.rank-pill {
  width: 34px;
  height: 34px;
  border-radius: 12px;
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #1d4ed8;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.profession-cell__title {
  font-weight: 700;
  color: #0f172a;
}

.profession-cell__subtitle {
  margin-top: 4px;
  color: #64748b;
  font-size: 0.82rem;
}

.count-cell {
  font-weight: 800;
  color: #0f172a;
  text-align: right;
}

.share-cell {
  min-width: 170px;
}

.share-cell__meta {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 0.82rem;
  color: #475569;
  margin-bottom: 8px;
}

.share-cell__bar {
  height: 8px;
  border-radius: 999px;
  background: #e2e8f0;
  overflow: hidden;
}

.share-cell__bar span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #2563eb 0%, #60a5fa 100%);
}

.modern-input :deep(.v-input__slot) {
  border-radius: 16px !important;
  min-height: 52px !important;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7);
}

.preservice-table :deep(table) {
  border-collapse: separate;
  border-spacing: 0 10px;
}

.preservice-table :deep(thead th) {
  font-size: 0.78rem !important;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 800 !important;
  color: #64748b !important;
  border-bottom: none !important;
}

.preservice-table :deep(tbody tr) {
  background: #ffffff;
}

.preservice-table :deep(tbody td) {
  border-bottom: none !important;
  padding-top: 16px !important;
  padding-bottom: 16px !important;
}

.preservice-table :deep(tbody tr td:first-child) {
  border-top-left-radius: 18px;
  border-bottom-left-radius: 18px;
}

.preservice-table :deep(tbody tr td:last-child) {
  border-top-right-radius: 18px;
  border-bottom-right-radius: 18px;
}

.preservice-table :deep(.v-data-footer) {
  border-top: none;
}

@media (max-width: 1264px) {
  .preservice-hero {
    flex-direction: column;
    align-items: flex-start;
  }

  .preservice-hero__right {
    width: 100%;
    text-align: left;
  }
}

@media (max-width: 960px) {
  .preservice-shell {
    padding: 16px;
  }

  .preservice-hero,
  .preservice-panel,
  .chart-card,
  .preservice-table-card {
    border-radius: 20px !important;
  }

  .panel-header,
  .section-header,
  .chart-card__header,
  .panel-header--table {
    flex-direction: column;
    align-items: flex-start;
  }

  .table-summary {
    justify-content: flex-start;
  }
}

@media (max-width: 600px) {
  .preservice-hero {
    padding: 22px 20px;
  }

  .preservice-hero__title {
    font-size: 1.35rem;
  }

  .summary-card__value {
    font-size: 1.75rem;
  }
}
</style>
